/** Background palettes. `dark` = Midnight (the original ink-blue), `light` = Paper-white. "system" follows the OS. */
export type Palette =
  | "qn"
  | "dark"
  | "black"
  | "graphite"
  | "warm"
  | "forest"
  | "ocean"
  | "wine"
  | "aurora"
  | "neon"
  | "contrast"
  | "light"
  | "paper"
  | "mist"
  | "sunrise";
export type ThemeMode = Palette | "system";

export interface PaletteInfo {
  id: Palette;
  label: string;
  dark: boolean;
  /** [background, surface, accent] for the preview card. */
  swatch: [string, string, string];
  /** Accent used when the user has not chosen their own. */
  accent: string;
}

export const PALETTES: PaletteInfo[] = [
  {
    id: "qn",
    label: "QN",
    dark: true,
    swatch: ["#070b0a", "#111a17", "#34d399"],
    accent: "#34d399",
  },
  {
    id: "dark",
    label: "Midnight",
    dark: true,
    swatch: ["#0a0e1a", "#121830", "#7c5cff"],
    accent: "#7c5cff",
  },
  {
    id: "black",
    label: "Black",
    dark: true,
    swatch: ["#000000", "#101010", "#e4e4e7"],
    accent: "#e4e4e7",
  },
  {
    id: "graphite",
    label: "Graphite",
    dark: true,
    swatch: ["#17181a", "#232629", "#8ab4f8"],
    accent: "#8ab4f8",
  },
  {
    id: "warm",
    label: "Warm",
    dark: true,
    swatch: ["#262624", "#30302e", "#d97757"],
    accent: "#d97757",
  },
  {
    id: "forest",
    label: "Forest",
    dark: true,
    swatch: ["#0b1410", "#14241d", "#34d399"],
    accent: "#34d399",
  },
  {
    id: "ocean",
    label: "Ocean",
    dark: true,
    swatch: ["#08121c", "#10243a", "#38bdf8"],
    accent: "#38bdf8",
  },
  {
    id: "wine",
    label: "Wine",
    dark: true,
    swatch: ["#160b10", "#29141c", "#fb7185"],
    accent: "#fb7185",
  },
  {
    id: "aurora",
    label: "Aurora",
    dark: true,
    swatch: ["#070d14", "#0f1f2c", "#2dd4bf"],
    accent: "#2dd4bf",
  },
  {
    id: "neon",
    label: "Neon",
    dark: true,
    swatch: ["#05020a", "#130b20", "#ff4fd8"],
    accent: "#ff4fd8",
  },
  {
    id: "contrast",
    label: "High contrast",
    dark: true,
    swatch: ["#000000", "#0a0a0a", "#ffd60a"],
    accent: "#ffd60a",
  },
  {
    id: "light",
    label: "Light",
    dark: false,
    swatch: ["#f5f4f0", "#ffffff", "#7c5cff"],
    accent: "#7c5cff",
  },
  {
    id: "paper",
    label: "Paper",
    dark: false,
    swatch: ["#faf9f5", "#ffffff", "#c96442"],
    accent: "#c96442",
  },
  {
    id: "mist",
    label: "Mist",
    dark: false,
    swatch: ["#f4f6f9", "#ffffff", "#2563eb"],
    accent: "#2563eb",
  },
  {
    id: "sunrise",
    label: "Sunrise",
    dark: false,
    swatch: ["#fff7f0", "#fffefc", "#f97316"],
    accent: "#f97316",
  },
];
export const isDarkPalette = (id: Palette) => PALETTES.find((p) => p.id === id)?.dark ?? true;
export type Density = "comfortable" | "compact";
export type FontScale = "sm" | "md" | "lg" | "xl";

/** Design language: modern (aurora glow), classic (flat, like ChatGPT / Claude), glass (translucent, iOS-like). */
export type DesignStyle = "modern" | "classic" | "glass";

export interface ThemePrefs {
  theme: ThemeMode;
  style: DesignStyle;
  accent: string;
  density: Density;
  fontScale: FontScale;
}

export const DEFAULT_THEME: ThemePrefs = {
  theme: "qn",
  style: "modern",
  accent: "#34d399",
  density: "comfortable",
  fontScale: "md",
};

export const ACCENT_PRESETS = [
  { name: "QN Green", value: "#34d399" },
  { name: "Violet", value: "#7c5cff" },
  { name: "Azure", value: "#3b82f6" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Emerald", value: "#22c55e" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Coral", value: "#fb6f5c" },
  { name: "Rose", value: "#ec4899" },
] as const;

const FONT_SCALE: Record<FontScale, number> = { sm: 0.925, md: 1, lg: 1.1, xl: 1.25 };

export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [124, 92, 255];
  const n = Number.parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((x) =>
      Math.round(Math.max(0, Math.min(255, x)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const [rr, gg, bb] = [r / 255, g / 255, b / 255];
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === rr
      ? (gg - bb) / d + (gg < bb ? 6 : 0)
      : max === gg
        ? (bb - rr) / d + 2
        : (rr - gg) / d + 4;
  return [h * 60, s, l];
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    hh < 60
      ? [c, x, 0]
      : hh < 120
        ? [x, c, 0]
        : hh < 180
          ? [0, c, x]
          : hh < 240
            ? [0, x, c]
            : hh < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

const lin = (v: number) => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const luminance = (hex: string) => {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};
/** WCAG contrast ratio between two colours. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const INK = "#0a0e1a";
/** Black or white text for an accent background: whichever is more readable. */
export function readableOn(hex: string): "#ffffff" | "#0a0e1a" {
  return contrast(hex, "#ffffff") >= contrast(hex, INK) ? "#ffffff" : INK;
}

/** Nudges an accent just enough that its button text reaches WCAG AA (4.5:1); returns it unchanged when it already does. */
export function solidAccent(hex: string): string {
  if (Math.max(contrast(hex, "#ffffff"), contrast(hex, INK)) >= 4.5) return hex;
  let [h, s, l] = rgbToHsl(...hexToRgb(hex));
  for (let i = 0; i < 40 && contrast(rgbToHex(...hslToRgb(h, s, l)), "#ffffff") < 4.5; i++)
    l -= 0.01;
  return rgbToHex(...hslToRgb(h, s, l));
}

/** Gradient partner: same saturation, hue shifted by +45° and slightly lighter. */
export function partnerAccent(hex: string): string {
  if (hex.toLowerCase() === DEFAULT_THEME.accent) return "#a3e635"; // QN Ventures brand pairing (emerald + lime)
  const [h, s, l] = rgbToHsl(...hexToRgb(hex));
  return rgbToHex(...hslToRgb(h + 45, Math.min(1, s * 0.95), Math.min(0.68, l + 0.06)));
}

export function resolveMode(theme: ThemeMode, prefersDark: boolean): Palette {
  return theme === "system" ? (prefersDark ? "dark" : "light") : theme;
}

/** Writes the theme onto <html>. Pure DOM, no framework. */
export function applyTheme(
  prefs: ThemePrefs,
  root: HTMLElement = document.documentElement,
  prefersDark?: boolean,
) {
  const dark =
    prefersDark ??
    (typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches);
  root.dataset.mode = resolveMode(prefs.theme, dark);
  root.dataset.density = prefs.density;
  root.dataset.style = prefs.style ?? "modern";
  // Each palette brings its own accent unless the user picked one: the default violet means "use the palette's".
  const mode = resolveMode(prefs.theme, dark);
  const own = prefs.accent.toLowerCase() !== DEFAULT_THEME.accent;
  const accent = own ? prefs.accent : (PALETTES.find((p) => p.id === mode)?.accent ?? prefs.accent);
  // Buttons must be readable (WCAG AA): nudge the accent just enough, then pick black or white text for it.
  const solid = solidAccent(accent);
  root.style.setProperty("--accent", solid);
  root.style.setProperty(
    "--accent-2",
    mode === "black" && !own ? "#a1a1aa" : partnerAccent(accent),
  );
  root.style.setProperty("--accent-fg", readableOn(solid));
  root.style.setProperty("--font-scale", String(FONT_SCALE[prefs.fontScale]));
}
